// Extract a visual contour from the MediaPipe segmentation mask.
//
// We don't need a topologically clean polyline (this isn't CAD); we just
// need a set of edge pixels we can render as a glowing outline over the
// video. Approach: a pixel is a "boundary" pixel if it is "person" AND
// at least one of its 4-connected neighbors is "not person" (or the
// image edge).
//
// This runs in one O(W*H) pass and is fed to a persistent Uint8Array
// buffer the caller owns, so no per-frame allocation.

export interface ContourResult {
  /** Flat W*H bitmap; 1 where pixel is on the silhouette boundary, else 0. */
  edgeMap: Uint8Array;
  /** Number of edge pixels found (for debugging). */
  edgeCount: number;
}

const THRESH = 128;

export function extractContour(
  mask: ImageData,
  edgeMap: Uint8Array
): ContourResult {
  const W = mask.width;
  const H = mask.height;
  const data = mask.data;

  edgeMap.fill(0);
  let edgeCount = 0;

  // Interior pass — skip the 1-pixel border so we don't need bounds checks
  // inside the hot loop. Border pixels can't be "meaningful" edges anyway.
  for (let y = 1; y < H - 1; y++) {
    const rowBase = y * W;
    for (let x = 1; x < W - 1; x++) {
      const p = rowBase + x;
      const center = data[p * 4];
      if (center < THRESH) continue;

      // Check 4-connected neighbors; if any is "not person", this is edge.
      const up = data[(p - W) * 4];
      const dn = data[(p + W) * 4];
      const lf = data[(p - 1) * 4];
      const rt = data[(p + 1) * 4];

      if (up < THRESH || dn < THRESH || lf < THRESH || rt < THRESH) {
        edgeMap[p] = 1;
        edgeCount++;
      }
    }
  }

  return { edgeMap, edgeCount };
}
